<script>
    import BreadcrumNav from "../../../components/breadcrum-nav.svelte";
    import { PowerTable } from "@muonw/powertable";
    import powerTableService from "../../../services/powerTable.js";
    import FindingCount from "../../finding/_components/finding-count.svelte";
    import {
        searchFindingPropertiesCountByKind,
        searchFindingPropertiesCount,
        findings,
    } from "../../finding/findings.state.svelte.js";
    import { assets_kinds } from "../../asset/assets.api.js";
    import tagService from "../../tag/service.js";
    import { format, locale } from "svelte-i18n";
    import { token } from "../../user/user.state.svelte";
    import { params, goto, url } from "@roxi/routify";
    import { onMount, tick } from "svelte";
    import dayjs from "dayjs";

    // --- Vanilla JS Replacements ---
    import TomSelect from "tom-select";
    import "tom-select/dist/css/tom-select.bootstrap5.css";
    import flatpickr from "flatpickr";
    import "flatpickr/dist/flatpickr.css";
    import { Spanish, English } from "flatpickr/dist/l10n/es.js";

    let loading = $state({ active: false });
    let data = [];
    let ptData = $state([]);

    params;
    $goto;
    $url;

    const redirect = (path, params) => $goto(path, params);

    // Active filters driven strictly by the URL
    let currentFilter = $derived($params ? $params : {});

    // SVELTE 5: Local UI state for form inputs before user clicks "Search"
    let filters = $state({
        asset__kind__in: [],
        finding__status__in: [],
        vulnerability__level__in: [],
        vulnerability__id__ilike: "",
        finding_tag_id: "",
        finding__first_discovery_at: "",
        finding__last_discovery_at: "",
    });

    const getTags = async () => await tagService.search("v2/tag/");

    const dateInputs = [
        {
            id: "finding__first_discovery_at",
            name: "finding__first_discovery_at",
            label: $format("components.advancedSearch.findings_created") || "First discovery at",
        },
        {
            id: "finding__last_discovery_at",
            name: "finding__last_discovery_at",
            label: $format("components.advancedSearch.findings_seen") || "Last discovery at",
        },
    ];

    let priorityOptions = [
        { name: $format("vulnerability.severity.critical").charAt(0).toUpperCase() + $format("vulnerability.severity.critical").slice(1), id: "critical" },
        { name: $format("vulnerability.severity.high").charAt(0).toUpperCase() + $format("vulnerability.severity.high").slice(1), id: "high" },
        { name: $format("vulnerability.severity.medium").charAt(0).toUpperCase() + $format("vulnerability.severity.medium").slice(1), id: "medium" },
        { name: $format("vulnerability.severity.low").charAt(0).toUpperCase() + $format("vulnerability.severity.low").slice(1), id: "low" },
        { name: $format("vulnerability.severity.info").charAt(0).toUpperCase() + $format("vulnerability.severity.info").slice(1), id: "info" },
    ];

    let vulKindOptions = [
        { name: $format("vulnerability.kind.ad"), id: "AD-" },
        { name: $format("vulnerability.kind.asvs"), id: "ASVS-" },
        { name: $format("vulnerability.kind.cve"), id: "CVE-" },
        { name: $format("vulnerability.kind.info"), id: "INFO-" },
        { name: $format("vulnerability.kind.leak"), id: "LEAK-" },
        { name: $format("vulnerability.kind.nvt"), id: "NVT-" },
        { name: $format("vulnerability.kind.sve"), id: "SVE-" },
        { name: $format("vulnerability.kind.eol"), id: "EOL-" },
        { name: $format("vulnerability.kind.dmarc"), id: "DMARC-" },
        { name: $format("vulnerability.kind.spf"), id: "SPF-" },
        { name: $format("vulnerability.kind.m365"), id: "M365-" },
        { name: $format("vulnerability.kind.code"), id: "CODE-" },
        { name: $format("vulnerability.kind.custom"), id: "X-" },
    ];

    let statusOptions = [
        { name: $format("finding.status.open"), id: "open" },
        { name: $format("finding.status.accepted"), id: "accepted" },
        { name: $format("finding.status.fixed"), id: "fixed" },
        { name: $format("finding.status.false_positive"), id: "false_positive" },
    ];

    let translatedAssetKinds = $derived(
        assets_kinds.map((opt) => ({
            id: opt.id,
            name: $format(`asset.kind.${opt.id}.singular`)
        }))
    );

    // Sync URL $params to local UI state
    $effect(() => {
        const p = currentFilter;

        const toArray = (val) => val ? (typeof val === "string" ? val.split(",") : val) : [];

        let first_gte = p.finding__first_discovery_at__gte ? dayjs(p.finding__first_discovery_at__gte).format("YYYY/MM/DD") : "";
        let first_lte = p.finding__first_discovery_at__lte ? dayjs(p.finding__first_discovery_at__lte).format("YYYY/MM/DD") : "";
        let last_gte = p.finding__last_discovery_at__gte ? dayjs(p.finding__last_discovery_at__gte).format("YYYY/MM/DD") : "";
        let last_lte = p.finding__last_discovery_at__lte ? dayjs(p.finding__last_discovery_at__lte).format("YYYY/MM/DD") : "";

        filters = {
            asset__kind__in: toArray(p.asset__kind__in),
            finding__status__in: toArray(p.finding__status__in),
            vulnerability__level__in: toArray(p.vulnerability__level__in),
            vulnerability__id__ilike: p.vulnerability__id__ilike || "",
            finding_tag_id: p.finding_tag_id || "",
            finding__first_discovery_at: first_gte && first_lte ? `${first_gte} - ${first_lte}` : "",
            finding__last_discovery_at: last_gte && last_lte ? `${last_gte} - ${last_lte}` : ""
        };
    });

    // --- TomSelect Svelte Action ---
    function tomSelectAction(node, params) {
        let currentValue = JSON.stringify(params.value || []);

        const formatOptions = (opts) => {
            return (opts || []).map((opt) => {
                if (typeof opt === "object" && opt !== null) {
                    return { id: opt.id, name: opt.name || opt.title_es || opt.title || opt.email || opt.id };
                }
                return { id: opt, name: opt };
            });
        };

        const ts = new TomSelect(node, {
            plugins: ["remove_button"],
            maxItems: null,
            valueField: "id",
            labelField: "name",
            searchField: "name",
            options: formatOptions(params.options),
            create: false,
            placeholder: params.placeholder || "Buscar...",
            onChange: (val) => {
                let formattedVal = !val ? [] : Array.isArray(val) ? val : [val];
                currentValue = JSON.stringify(formattedVal);
                params.onChange(formattedVal);
            },
        });

        if (params.value && params.value.length) {
            ts.setValue(params.value, true);
        }

        return {
            update(newParams) {
                params = newParams;
                const newString = JSON.stringify(newParams.value || []);
                if (newString !== currentValue) {
                    currentValue = newString;
                    ts.setValue(newParams.value || [], true);
                }
            },
            destroy() {
                ts.destroy();
            }
        };
    }

    function flatpickrRangeAction(node, params) {
        let currentValue = params.value || "";

        const fp = flatpickr(node, {
            mode: "range",
            dateFormat: "Y/m/d",
            locale: $locale === "es" ? Spanish : English,
            defaultDate: params.value ? params.value.split(" - ") : [],
            onChange: (selectedDates) => {
                // Force strict manual formatting to avoid Spanish " a " or English " to " strings
                if (selectedDates.length === 2) {
                    const formatDate = (d) => {
                        let y = d.getFullYear();
                        let m = String(d.getMonth() + 1).padStart(2, "0");
                        let day = String(d.getDate()).padStart(2, "0");
                        return `${y}/${m}/${day}`;
                    };

                    const internalValue = `${formatDate(selectedDates[0])} - ${formatDate(selectedDates[1])}`;
                    currentValue = internalValue;
                    params.onChange(internalValue);
                } else if (selectedDates.length === 0) {
                    currentValue = "";
                    params.onChange("");
                }
            },
            onClose: (selectedDates, dateStr, instance) => {
                if (selectedDates.length === 1) {
                    instance.clear();
                    currentValue = "";
                    params.onChange("");
                }
            },
        });

        return {
            update(newParams) {
                params = newParams;
                if (fp.isOpen) return;

                if (newParams.value === currentValue) return;
                currentValue = newParams.value || "";

                if (newParams.value) {
                    fp.setDate(newParams.value.split(" - "), false);
                } else {
                    fp.clear(false);
                }
            },
            destroy() {
                fp.destroy();
            },
        };
    }

    function handleFilterChange() {
        let updatedFilter = { ...$params };

        if (filters.asset__kind__in && filters.asset__kind__in.length > 0) {
            updatedFilter.asset__kind__in = filters.asset__kind__in.join(",");
        } else {
            delete updatedFilter.asset__kind__in;
        }

        if (filters.finding__status__in && filters.finding__status__in.length > 0) {
            updatedFilter.finding__status__in = filters.finding__status__in.join(",");
        } else {
            delete updatedFilter.finding__status__in;
        }

        if (filters.vulnerability__level__in && filters.vulnerability__level__in.length > 0) {
            updatedFilter.vulnerability__level__in = filters.vulnerability__level__in.join(",");
        } else {
            delete updatedFilter.vulnerability__level__in;
        }

        if (filters.vulnerability__id__ilike) {
            updatedFilter.vulnerability__id__ilike = filters.vulnerability__id__ilike;
        } else {
            delete updatedFilter.vulnerability__id__ilike;
        }

        if (filters.finding_tag_id) {
            updatedFilter.finding_tag_id = filters.finding_tag_id;
        } else {
            delete updatedFilter.finding_tag_id;
        }

        // Date processing
        if (filters.finding__first_discovery_at && filters.finding__first_discovery_at.includes(" - ")) {
            let dates = filters.finding__first_discovery_at.split(" - ");
            updatedFilter.finding__first_discovery_at__gte = dates[0].replaceAll("/", "-") + "T00:00:00.000-03:00";
            updatedFilter.finding__first_discovery_at__lte = dates[1].replaceAll("/", "-") + "T23:59:59.999-03:00";
            delete updatedFilter.finding__first_discovery_at;
        } else {
            delete updatedFilter.finding__first_discovery_at__gte;
            delete updatedFilter.finding__first_discovery_at__lte;
        }

        if (filters.finding__last_discovery_at && filters.finding__last_discovery_at.includes(" - ")) {
            let dates = filters.finding__last_discovery_at.split(" - ");
            updatedFilter.finding__last_discovery_at__gte = dates[0].replaceAll("/", "-") + "T00:00:00.000-03:00";
            updatedFilter.finding__last_discovery_at__lte = dates[1].replaceAll("/", "-") + "T23:59:59.999-03:00";
            delete updatedFilter.finding__last_discovery_at;
        } else {
            delete updatedFilter.finding__last_discovery_at__gte;
            delete updatedFilter.finding__last_discovery_at__lte;
        }

        redirect(".", updatedFilter);
    }

    const clearFilters = async () => {
        filters = {
            asset__kind__in: [],
            finding__status__in: [],
            vulnerability__level__in: [],
            vulnerability__id__ilike: "",
            finding_tag_id: "",
            finding__first_discovery_at: "",
            finding__last_discovery_at: "",
        };
        redirect(".");
    };

    const clearDateFilter = (fieldName) => {
        filters[fieldName] = "";
    };

    const parseRows = (pageContent) => {
        const cleanParams = handleEmptyFilters(currentFilter);
        const queryStr = new URLSearchParams(cleanParams).toString();
        pageContent.forEach((row) => {
            if (row.kind) {
                const targetUrl = `/property/finding/${row.kind}${queryStr ? "?" + queryStr : ""}`;
                row.kind = `<a href="${targetUrl}">${row.kind}</a>`;
            }
        });
        return pageContent;
    };

    let ptOptions = $state({
        uniquePrefix: "findingPropKindTable",
        rowsPerPageOptions: [5, 10, 20, 50, 100],
        rowsPerPage: 10,
        footerText: false,
        footerFilters: false,
        footerLoadingBar: false,
        segments: {
            topBar: ["search"],
            pTable: ["table"],
            bottomBar: ["dropdown", "stats", "pagination"],
        },
        translations: {
            add: $format("list.interface.add"),
            delete: $format("list.interface.delete"),
            filterBy: $format("list.interface.filterBy"),
            from: $format("list.interface.from"),
            invertSelection: $format("list.interface.invertSelection"),
            next: $format("list.interface.next"),
            of: $format("list.interface.of"),
            previous: $format("list.interface.previous"),
            rows: $format("list.interface.rows"),
            search: $format("list.interface.search"),
            selectAll: $format("list.interface.selectAll"),
            selectNone: $format("list.interface.selectNone"),
        },
        isDataRemote: false,
        userFunctions: {
            customParse: parseRows,
        },
    });

    let ptInstructs = [
        {
            key: "kind",
            title: $format("list.columns.kind"),
            filterable: false,
            parseAs: "unsafe-html",
        },
        {
            key: "count",
            title: $format("list.columns.amount"),
            filterable: false,
            userFunctions: { customSort: powerTableService.sortNumbers },
        },
    ];

    const handleEmptyFilters = (filterObj) => {
        let clean = { ...filterObj };
        for (let [key, val] of Object.entries(clean)) {
            if (val === "" || val === null || val === undefined) {
                delete clean[key];
            }
        }
        return clean;
    };

    const getData = async (config = {}) => {
        if (!token.access_token) return [];

        let tempPtData = [];
        data = await searchFindingPropertiesCountByKind(config);
        for (let [key, count] of Object.entries(data)) {
            tempPtData.push({ kind: key, count: count });
        }
        tempPtData.sort((a, b) => b.count - a.count);
        ptData = tempPtData;
        loading.active = false;
        return ptData;
    };

    const getTotalCount = async (config = {}) => {
        if (!token.access_token) return 0;
        config.filter = handleEmptyFilters(config.filter);
        return await searchFindingPropertiesCount(config);
    };
</script>

<section class="content-header">
    <div class="container-fluid">
        <div class="row">
            <div class="col-6">
                <h1 class="mt-3 mb-3 page-title">
                    <i class="fas fa-shapes fa-xs"></i>
                    {$format("page.properties.finding_props")}
                </h1>
            </div>
            <div class="col-6">
                <BreadcrumNav
                    routes={[
                        {
                            label: $format("findings"),
                            path: "/finding",
                            params: {
                                status__in: "open,accepted",
                                vulnerability__level__in: "critical,high",
                            },
                        },
                        { label: $format("page.properties.finding_props") },
                    ]}
                />
            </div>
        </div>
    </div>
</section>
<section class="content">
    <div class="container-fluid">
        {#key currentFilter}
            <div class="card mt-3 card-secondary card-outline no-export">
                <div class="card-header">
                    <h4 class="card-title">
                        {$format("components.advancedSearch.search")}
                    </h4>
                </div>
                <div class="card-body pe-5">
                    <!-- First Row of Selects -->
                    <div class="row mb-3">
                        <div class="col-12 col-md-4">
                            <label for="vulnerability__level__in" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                {$format("vulnerability.severity.title")}
                            </label>
                            <select
                                id="vulnerability__level__in"
                                name="vulnerability__level__in"
                                multiple
                                use:tomSelectAction={{
                                    options: priorityOptions,
                                    value: filters.vulnerability__level__in,
                                    placeholder: $format("buttons.search"),
                                    onChange: (val) => (filters.vulnerability__level__in = val),
                                }}
                            ></select>
                        </div>
                        <div class="col-12 col-md-4">
                            <label for="vulnerability__id__ilike" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                {$format("components.advancedSearch.vulnerability_kind")}
                            </label>
                            <select
                                class="form-select"
                                name="vulnerability__id__ilike"
                                bind:value={filters.vulnerability__id__ilike}
                            >
                                <option value="">--</option>
                                {#each vulKindOptions as opt}
                                    <option value={opt.id}>{opt.name}</option>
                                {/each}
                            </select>
                        </div>
                        <div class="col-12 col-md-4">
                            {#await getTags()}
                                <label for="finding_tag_id" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                    {$format("components.advancedSearch.finding_tag")}
                                </label>
                                <select class="form-select" disabled>
                                    <option value="">--</option>
                                </select>
                            {:then tags}
                                <label for="finding_tag_id" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                    {$format("components.advancedSearch.finding_tag")}
                                </label>
                                <select
                                    class="form-select"
                                    name="finding_tag_id"
                                    bind:value={filters.finding_tag_id}
                                >
                                    <option value="">--</option>
                                    {#each tags as opt}
                                        <option value={opt.id}>{opt.name}</option>
                                    {/each}
                                </select>
                            {/await}
                        </div>
                    </div>

                    <!-- Second Row of Selects -->
                    <div class="row mb-3">
                        <div class="col-12 col-md-6">
                            <label for="asset__kind__in" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                {$format("components.advancedSearch.asset_kind")}
                            </label>
                            <select
                                id="asset__kind__in"
                                name="asset__kind__in"
                                multiple
                                use:tomSelectAction={{
                                    options: translatedAssetKinds,
                                    value: filters.asset__kind__in,
                                    placeholder: $format("buttons.search"),
                                    onChange: (val) => (filters.asset__kind__in = val),
                                }}
                            ></select>
                        </div>
                        <div class="col-12 col-md-6">
                            <label for="finding__status__in" class="form-label text-muted fw-bold mb-1" style="font-size: 0.85rem;">
                                {$format("finding.status.title")}
                            </label>
                            <select
                                id="finding__status__in"
                                name="finding__status__in"
                                multiple
                                use:tomSelectAction={{
                                    options: statusOptions,
                                    value: filters.finding__status__in,
                                    placeholder: $format("buttons.search"),
                                    onChange: (val) => (filters.finding__status__in = val),
                                }}
                            ></select>
                        </div>
                    </div>

                    <!-- Dates Row -->
                    <div class="row mb-3">
                        {#each dateInputs as field}
                            <div class="col-12 col-md-6">
                                <div class="d-flex justify-content-between align-items-end mb-1">
                                    <label for={field.id} class="form-label text-muted fw-bold mb-0" style="font-size: 0.85rem;">
                                        {field.label}
                                    </label>
                                    <a
                                        href="#"
                                        class="text-danger text-decoration-none"
                                        style="font-size: 0.75rem;"
                                        onclick={(e) => {
                                            e.preventDefault();
                                            clearDateFilter(field.name);
                                        }}
                                    >
                                        {$format("clean")}
                                    </a>
                                </div>
                                <div class="input-group input-group-sm">
                                    <span class="input-group-text bg-light text-muted">
                                        <i class="far fa-calendar-alt"></i>
                                    </span>
                                    <input
                                        type="text"
                                        class="form-control bg-white cursor-pointer"
                                        id={field.id}
                                        name={field.name}
                                        placeholder="YYYY/MM/DD - YYYY/MM/DD"
                                        readonly
                                        use:flatpickrRangeAction={{
                                            value: filters[field.name],
                                            onChange: (val) => (filters[field.name] = val),
                                        }}
                                    />
                                </div>
                            </div>
                        {/each}
                    </div>

                    <div class="btn-strip pt-3 mt-3 border-top pb-3">
                        <button class="btn btn-primary btn-sm px-4 me-2" onclick={handleFilterChange}>
                            <i class="fas fa-search me-1"></i> {$format("buttons.search")}
                        </button>
                        <button class="btn btn-light btn-sm px-4 border" onclick={clearFilters}>
                            <i class="fas fa-eraser me-1"></i> {$format("buttons.reset")}
                        </button>
                    </div>
                </div>
            </div>
        {/key}

        {#await getData({ filter: currentFilter, from: "html" })}
            <div class="card mt-3">
                <div class="card-body" style="min-height: 500px; display: flex; flex-direction: column; align-items: center; justify-content: center;">
                    <div class="overlay">
                        <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                    </div>
                </div>
            </div>
        {:then success}
            <div class="row">
                <div class="col-12 d-flex flex-wrap flex-md-nowrap" style="gap: 15px;">
                    {#if !loading.active}
                        <div class="card mt-3 w-100" style="flex: 3; align-self: stretch;">
                            <div class="card-header">
                                {$format("finding.properties.title")}
                            </div>
                            <div class="card-body">
                                <div class="table-responsive">
                                    <FindingCount
                                        data={ptData}
                                        field={"kind"}
                                        filter={currentFilter}
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="card mt-3 w-100" style="flex: 1;">
                            <div class="card-header">
                                <div class="card-title">
                                    {$format("finding.properties.total_amount")}
                                </div>
                            </div>
                            <div class="card-body" style="display: flex;">
                                <div class="info-box" style="box-shadow: none !important; align-self: center; justify-content: center;">
                                    {#await getTotalCount( { filter: currentFilter } )}
                                        <div class="overlay bg-light">
                                            <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                                        </div>
                                    {:then count}
                                        <span class="number">{count}</span>
                                    {/await}
                                </div>
                            </div>
                        </div>
                    {:else}
                        <div class="card mt-3 w-100" style="min-height: 500px;">
                            <div class="card-body" style="display: flex; justify-content: center;">
                                <div class="overlay">
                                    <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                                </div>
                            </div>
                        </div>
                    {/if}
                </div>
            </div>
            <div class="row">
                <div class="col-12">
                    {#if !loading.active}
                        <div class="card mt-3">
                            <div class="card-header">
                                {$format("finding.properties.title")}
                            </div>
                            <div class="card-body">
                                <div class="table-responsive">
                                    {#key JSON.stringify(currentFilter)}
                                        <PowerTable
                                            {ptData}
                                            {ptInstructs}
                                            {ptOptions}
                                        />
                                    {/key}
                                </div>
                            </div>
                        </div>
                    {:else}
                        <div class="card mt-3" style="min-height: 500px;">
                            <div class="card-body" style="display: flex; justify-content: center;">
                                <div class="overlay">
                                    <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                                </div>
                            </div>
                        </div>
                    {/if}
                </div>
            </div>
        {/await}
    </div>
</section>

<style>
    .number {
        font-size: 3.5em;
        text-align: center;
        display: flex;
        flex-direction: column;
        align-content: center;
        justify-content: center;
    }

    .cursor-pointer {
        cursor: pointer !important;
    }

    /* Bootstrap 5 theme overrides for TomSelect tags */
    :global(.ts-control) {
        border-radius: 0.375rem !important;
        padding: 0.375rem 0.75rem !important;
        min-height: 38px !important;
    }

    :global(.ts-wrapper) {
        z-index: 100;
    }
</style>